import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminUsers, adminPatchUser, adminDeleteUser, adminUsersExport } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Search, Download, Trash2, KeyRound, Phone, Save, X, CheckCircle2, XCircle } from "lucide-react";

function getSubscriptionDisplay(user) {
  const sub = user.subscription || {};
  const rawStatus = String(sub.status || user.subscription_status || "inactive").toLowerCase();
  const rawPlan = sub.plan || user.plan || user.plan_id || "";
  const expiresAt = sub.expires_at || user.subscription_expires_at;
  const daysRemaining = sub.days_remaining ?? (expiresAt ? Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000)) : 0);

  let planLabel = rawPlan || "No plan";
  if (planLabel === "trial") planLabel = "Trial";
  else if (planLabel === "monthly") planLabel = "Monthly";
  else if (planLabel === "quarterly") planLabel = "Quarterly";
  else if (planLabel === "half_yearly") planLabel = "Half yearly";
  else if (planLabel === "yearly") planLabel = "Yearly";

  let statusLabel = "Inactive";
  if (rawStatus === "active") statusLabel = "Active";
  else if (rawStatus === "trial") statusLabel = "Trial";
  else if (rawStatus === "expired") statusLabel = "Expired";
  else if (rawStatus === "pending") statusLabel = "Pending";
  else if (rawStatus === "paused") statusLabel = "Paused";
  else if (rawStatus === "canceled" || rawStatus === "cancelled") statusLabel = "Canceled";

  return {
    planLabel,
    statusLabel,
    daysRemaining,
    expiresAt: expiresAt ? new Date(expiresAt).toLocaleDateString("en-GB") : "—",
  };
}

export default function UsersTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", q],
    queryFn: () => adminUsers(q ? { q } : {}),
  });

  const patchMut = useMutation({
    mutationFn: ({ id, body }) => adminPatchUser(id, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-users"] }); toast({ title: "User updated" }); },
  });
  const deleteMut = useMutation({
    mutationFn: (id) => adminDeleteUser(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-users"] }); toast({ title: "User deleted" }); },
  });

  const onExport = async (range) => {
    try { await adminUsersExport(range); toast({ title: `Exported ${range} CSV` }); }
    catch (e) { toast({ variant: "destructive", title: "Export failed", description: e?.message || "" }); }
  };

  return (
    <div className="space-y-3" data-testid="admin-users-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 relative min-w-[200px] max-w-md">
          <Search className="size-4 absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="admin-users-search"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name / email / phone…"
            className="w-full bg-ink-800 border border-white/10 rounded pl-8 pr-3 py-1.5 text-sm"
          />
        </div>
        <button data-testid="admin-users-export-week"
          onClick={() => onExport("week")}
          className="text-xs px-3 py-1.5 rounded bg-white/5 hover:bg-white/10 inline-flex items-center gap-1">
          <Download className="size-3.5" /> Weekly CSV
        </button>
        <button data-testid="admin-users-export-month"
          onClick={() => onExport("month")}
          className="text-xs px-3 py-1.5 rounded bg-white/5 hover:bg-white/10 inline-flex items-center gap-1">
          <Download className="size-3.5" /> Monthly CSV
        </button>
        <button data-testid="admin-users-export-all"
          onClick={() => onExport("all")}
          className="text-xs px-3 py-1.5 rounded bg-amber-400/90 text-ink-900 inline-flex items-center gap-1">
          <Download className="size-3.5" /> Full CSV
        </button>
      </div>

      {isLoading ? <Loader2 className="size-5 animate-spin text-white/50" /> : (
        <div className="rounded-xl border border-white/10 overflow-x-auto" data-testid="admin-users-table">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-xs uppercase tracking-widest text-white/40">
              <tr>
                <th className="px-3 py-2 text-left">Name / Email</th>
                <th className="px-3 py-2 text-left">Phone</th>
                <th className="px-3 py-2 text-left">Role · Status</th>
                <th className="px-3 py-2 text-left">Plan · Subscription</th>
                <th className="px-3 py-2 text-left">Trial / Extend</th>
                <th className="px-3 py-2 text-left">Password</th>
                <th className="px-3 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {(data?.users || []).map((u) => (
                <UserRow key={u.id} user={u}
                  onPatch={(body) => patchMut.mutate({ id: u.id, body })}
                  onDelete={() => {
                    if (window.confirm(`Delete ${u.email}? This cannot be undone.`)) deleteMut.mutate(u.id);
                  }}
                />
              ))}
              {!(data?.users || []).length && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-white/40 text-sm">
                  {q ? `No users matching "${q}"` : "No users yet."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UserRow({ user, onPatch, onDelete }) {
  const [trial, setTrial] = useState(user.trial_days ?? 7);
  const [extend, setExtend] = useState(0);
  const [plan, setPlan] = useState(user.plan || "monthly");
  const [phone, setPhone] = useState(user.phone || "");
  const [editPhone, setEditPhone] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [status, setStatus] = useState(user.status);

  return (
    <tr className="bg-white/[0.02]" data-testid={`admin-user-row-${user.id}`}>
      <td className="px-3 py-2 align-top min-w-[180px]">
        <div className="text-white/90 font-medium">{user.name}</div>
        <div className="text-xs text-white/40 break-all">{user.email}</div>
      </td>
      <td className="px-3 py-2 align-top text-xs">
        {editPhone ? (
          <div className="flex items-center gap-1">
            <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+91…"
              className="w-32 bg-ink-800 border border-white/10 rounded px-1.5 py-0.5" />
            <button onClick={() => { onPatch({ phone }); setEditPhone(false); }} className="text-emerald-300"><Save className="size-3.5" /></button>
            <button onClick={() => { setEditPhone(false); setPhone(user.phone || ""); }} className="text-white/40"><X className="size-3.5" /></button>
          </div>
        ) : (
          <button onClick={() => setEditPhone(true)} className="text-white/70 hover:text-amber-200 inline-flex items-center gap-1">
            <Phone className="size-3" /> {user.phone || <span className="text-white/30">add…</span>}
          </button>
        )}
      </td>
      <td className="px-3 py-2 align-top text-xs">
        <select defaultValue={user.role} onChange={(e) => onPatch({ role: e.target.value })}
          className="bg-ink-800 border border-white/10 rounded px-1.5 py-0.5">
          <option value="user">user</option>
          <option value="admin">admin</option>
        </select>
        {status === "PENDING" ? (
          <div className="flex items-center gap-1 mt-1">
            <button
              onClick={() => { setStatus("APPROVED"); onPatch({ status: "APPROVED" }); }}
              data-testid={`admin-approve-${user.id}`}
              className="text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-0.5"
              title="Approve user">
              <CheckCircle2 className="size-3.5" /> Approve
            </button>
            <button
              onClick={() => { setStatus("REJECTED"); onPatch({ status: "REJECTED" }); }}
              data-testid={`admin-reject-${user.id}`}
              className="text-red-300 hover:text-red-200 inline-flex items-center gap-0.5"
              title="Reject user">
              <XCircle className="size-3.5" /> Reject
            </button>
          </div>
        ) : (
          <div className="text-white/40 mt-1">{status}</div>
        )}
      </td>
      <td className="px-3 py-2 align-top text-xs">
        {(() => {
          const sub = getSubscriptionDisplay(user);
          const statusClass = sub.statusLabel === "Active" ? "text-emerald-300" : sub.statusLabel === "Trial" ? "text-amber-200" : "text-red-300";
          return (
            <>
              <div className="text-amber-200">{sub.planLabel}</div>
              <div className={statusClass}>{sub.statusLabel} ({sub.daysRemaining}d)</div>
              <div className="text-white/40">exp: {sub.expiresAt}</div>
            </>
          );
        })()}
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex flex-col gap-1 text-xs">
          <div className="flex items-center gap-1">
            <span className="text-white/40">Trial(d):</span>
            <input type="number" min={0} max={365} value={trial} onChange={(e) => setTrial(Number(e.target.value))}
              className="w-14 bg-ink-800 border border-white/10 rounded px-1.5 py-0.5" />
            <button onClick={() => onPatch({ trial_days: trial })} className="px-2 py-0.5 rounded bg-white/10 hover:bg-white/20">Set</button>
          </div>
          <div className="flex items-center gap-1">
            <select value={plan} onChange={(e) => setPlan(e.target.value)} className="bg-ink-800 border border-white/10 rounded px-1.5 py-0.5">
              <option value="monthly">monthly</option>
              <option value="quarterly">quarterly</option>
              <option value="half_yearly">half_yearly</option>
              <option value="yearly">yearly</option>
            </select>
            <input type="number" value={extend} onChange={(e) => setExtend(Number(e.target.value))} placeholder="±d"
              className="w-12 bg-ink-800 border border-white/10 rounded px-1.5 py-0.5" />
            <button onClick={() => onPatch({ plan_key: plan, extend_subscription_days: extend })}
              data-testid={`admin-extend-${user.id}`}
              className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-200">Extend</button>
          </div>
        </div>
      </td>
      <td className="px-3 py-2 align-top text-xs">
        {showPw ? (
          <div className="flex items-center gap-1">
            <input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)}
              placeholder="new password"
              className="w-32 bg-ink-800 border border-white/10 rounded px-1.5 py-0.5" />
            <button onClick={() => { if (newPw.length >= 8) { onPatch({ new_password: newPw }); setShowPw(false); setNewPw(""); } }}
              data-testid={`admin-pw-save-${user.id}`}
              className={newPw.length >= 8 ? "text-emerald-300" : "text-white/40"} disabled={newPw.length < 8}><Save className="size-3.5" /></button>
            <button onClick={() => { setShowPw(false); setNewPw(""); }} className="text-white/40"><X className="size-3.5" /></button>
          </div>
        ) : (
          <button onClick={() => setShowPw(true)}
            data-testid={`admin-pw-reset-${user.id}`}
            className="text-white/70 hover:text-amber-200 inline-flex items-center gap-1">
            <KeyRound className="size-3" /> reset
          </button>
        )}
        <div className="text-[10px] text-white/30 mt-1">stored as bcrypt hash</div>
      </td>
      <td className="px-3 py-2 align-top">
        <button onClick={onDelete}
          data-testid={`admin-user-delete-${user.id}`}
          className="text-red-300 hover:text-red-200 inline-flex items-center gap-1 text-xs">
          <Trash2 className="size-3.5" /> Delete
        </button>
      </td>
    </tr>
  );
}
